// @mui icons
// @mui material components
import { Box } from '@mui/material';
import Card from '@mui/material/Card';

// Images
// import bgImage from 'assets/images/bg-sign-in-basic.jpeg';
import bgImage from 'assets/images/sign-in-bg.jpeg';
//  React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';
// Authentication layout components
import BasicLayout from 'layouts/authentication/components/BasicLayout';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { brandColors } from 'types/colors';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { resetPasswordWithToken } from '../../stores/sensor/sensorStore';

function Basic() {
  const [pwd, setPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [show, setShow] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false); 

  const submittedForm = useRef(false);

  // NEW: local error UI state + timer ref
  const [errorMsg, setErrorMsg] = useState('');
  const [showError, setShowError] = useState(false);
  const hideTimerRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const resetPassword = async (token, email, pwd) => {
    try {
      await dispatch(resetPasswordWithToken({ token, email, newPassword: pwd })).unwrap();
      navigate('/authentication/sign-in');
    } catch (err) {
      // Handle both string errors (from rejectWithValue) and Error objects
      const msg = typeof err === 'string' ? err : err?.message || 'Failed to reset password';
      setErrorMsg(String(msg));
      setShowError(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => setShowError(false), 4000);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/authentication/sign-in');
    }
  }, [token, navigate]);

  useEffect(() => {
    submittedForm.current = true;
    // NEW: cleanup timer on unmount
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();

    // Validate password length
    if (pwd.length < 6) {
      setErrorMsg('Password must be at least 6 characters long');
      setShowError(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => setShowError(false), 4000);
      return;
    }

    // Validate passwords match
    if (pwd !== confirmPwd) {
      setErrorMsg('Passwords do not match');
      setShowError(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => setShowError(false), 4000);
      return;
    }

    await resetPassword(token, email, pwd);
  };

  return (
    <BasicLayout
      image={bgImage}
      sx={{
        overflow: 'hidden',
      }}>
      <Card>
        <MDBox
          variant="gradient"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          pb={3}
          textAlign="center"
          sx={{
            backgroundColor: brandColors.BLUE,
          }}>
          <Box
            component="img"
            src="/logo_white.svg"
            alt="Logo"
            sx={{
              height: 90,
              marginRight: 2,
              scale: '1.2',
            }}
          />
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}></MDTypography>
        </MDBox>

        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form">
            <MDBox mb={2}>
              <MDInput
                value={pwd}
                name="password"
                type={show ? 'text' : 'password'}
                label="New Password"
                onChange={e => setPwd(e.target.value)}
                fullWidth
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={show ? 'Hide password' : 'Show password'}
                        onClick={() => setShow(s => !s)}
                        onMouseDown={e => e.preventDefault()}
                        edge="end">
                        {show ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>

            <MDBox mb={0}>
              <MDInput
                value={confirmPwd}
                name="confirmPassword"
                type={showConfirm ? 'text' : 'password'}
                label="Confirm Password"
                onChange={e => setConfirmPwd(e.target.value)}
                fullWidth
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showConfirm ? 'Hide password' : 'Show password'}
                        onClick={() => setShowConfirm(s => !s)}
                        onMouseDown={e => e.preventDefault()}
                        edge="end">
                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>

            {/* Error message box stays the same */}
            <MDBox
              role="alert"
              aria-live="polite"
              sx={{
                mt: showError ? 0.5 : 0,
                maxHeight: showError ? 48 : 0,
                opacity: showError ? 1 : 0,
                overflow: 'hidden',
                transition: 'opacity 400ms ease, max-height 400ms ease, margin-top 400ms ease',
              }}>
              <MDTypography
                variant="caption"
                sx={{ color: 'error.main', display: 'block', fontWeight: '600', fontSize: '.9rem' }}>
                {errorMsg}
              </MDTypography>
            </MDBox>

            <MDBox mt={3} mb={1}>
              <MDButton
                onClick={handleSubmit}
                fullWidth
                disableRipple
                sx={{
                  backgroundColor: brandColors.BLUE,
                  color: '#fff',
                  transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                  '&:hover': {
                    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.3)',
                    backgroundColor: `${brandColors.BLUE} !important`,
                  },
                  '&:active': {
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                    backgroundColor: `${brandColors.BLUE} !important`,
                  },
                  '&:focus': {
                    backgroundColor: `${brandColors.BLUE} !important`,
                  },
                }}>
                update password
              </MDButton>
            </MDBox>
            {/* link to sign-in */}
            <MDBox mt={2} textAlign="center">
              <MDTypography
                variant="button"
                color="text"
                sx={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  '&:hover': { color: brandColors.BLUE },
                }}
                onClick={() => {
                  navigate('/authentication/sign-in');
                }}>
                Back to sign in
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
